import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 14 — Profile Commercial Center and SignWell.
 * Mirrors messagingQueries.js/fileQueries.js's `request()` error
 * contract exactly (`.status`/`.body` on thrown errors). Every
 * commercial DOCUMENT download goes through the EXISTING
 * `fetchSignedUrl` from fileQueries.js — this module never invents its
 * own download endpoint, it only ever hands back an `fas_id` pointer.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const cqk = {
  summary: (wrkId) => ["commercial-summary", wrkId],
  signature: (wrkId, ctrId) => ["commercial-signature", wrkId, ctrId],
  comments: (wrkId, ctrId) => ["commercial-comments", wrkId, ctrId],
};

export function useCommercialSummary(workspaceId) {
  return useQuery({
    queryKey: cqk.summary(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/summary`),
    refetchInterval: 15000,
  });
}

export function useContractSignature(workspaceId, ctrId, { enabled = true } = {}) {
  return useQuery({
    queryKey: cqk.signature(workspaceId, ctrId),
    enabled: !!workspaceId && !!ctrId && enabled,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/contracts/${ctrId}/signature`),
    refetchInterval: 6000,
  });
}

export function useStartSignature(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ctrId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/contracts/${ctrId}/start-signature`),
    onSuccess: (_data, ctrId) => {
      qc.invalidateQueries({ queryKey: cqk.signature(workspaceId, ctrId) });
      qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) });
    },
  });
}

export function useContractComments(workspaceId, ctrId) {
  return useQuery({
    queryKey: cqk.comments(workspaceId, ctrId),
    enabled: !!workspaceId && !!ctrId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/contracts/${ctrId}/comments`),
  });
}

export function useAddContractComment(workspaceId, ctrId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/contracts/${ctrId}/comments`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.comments(workspaceId, ctrId) }),
  });
}

export function useSubmitChangeRequest(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ctrId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/contracts/${ctrId}/change-requests`),
    onSuccess: (_data, ctrId) => {
      qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) });
      qc.invalidateQueries({ queryKey: cqk.signature(workspaceId, ctrId) });
    },
  });
}

export function useOpenContractDiscussion(workspaceId) {
  return useMutation({
    mutationFn: (ctrId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/contracts/${ctrId}/message-thread`),
  });
}

// -- Coach-only manual entry -------------------------------------------------
export function useUpsertPackage(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/package`, { method: "PUT", body: JSON.stringify(payload) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) }),
  });
}

export function useUploadPackageDocument(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file) => {
      const form = new FormData();
      form.append("upload", file, file.name);
      return request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/package/document`, { method: "POST", body: form });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) }),
  });
}

export function useCreatePayment(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/payments`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) }),
  });
}

export function useDeletePayment(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pmtId) => request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/payments/${pmtId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) }),
  });
}

export function useCreateInvoice(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, ...fields }) => {
      const form = new FormData();
      form.append("upload", file, file.name);
      Object.entries(fields).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") form.append(k, v); });
      return request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/invoices`, { method: "POST", body: form });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) }),
  });
}

export function useDeleteInvoice(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invId) => request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/invoices/${invId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) }),
  });
}

export function useCreateContract(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, ...fields }) => {
      const form = new FormData();
      form.append("upload", file, file.name);
      Object.entries(fields).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") form.append(k, v); });
      return request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/contracts`, { method: "POST", body: form });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) }),
  });
}

export function useDeleteContract(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ctrId) => request(`/api/rehearsal-room/workspaces/${workspaceId}/commercial/contracts/${ctrId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cqk.summary(workspaceId) }),
  });
}
